import {
  all,
  select,
  take,
  put,
  call,
  takeEvery,
  debounce,
} from 'redux-saga/effects';
import { damzOnline } from '../actions/initialization';
import {
  addDraftAttachment,
  addDraftUser,
  allowDraftMessage,
  disallowDraftMessage,
  replaceUnfurlLog,
  markUnfurlFinished,
  markUnfurlResolving,
  maybeUnfurlLinks,
  removeDraftAttachment,
  removeDraftUser,
  logDeviationsNotFound,
} from '../actions/drafts';
import {
  getDraftParticipants,
  getIsUnfurlResolving,
  getUnfurlLogForRoom,
  getDraftMessageAttachments,
  getDeviationsNotFound,
} from '../selectors/drafts';
import { elmoSaysThisMightBeADeviationUrl } from '@wix/da-shared-react/pkg/utils/deviations';
import {
  PapiDamzCanMessage,
  PapiDaSharedDeviationMultifetch,
  PapiRequestDamzCanMessage,
  PapiRequestDaSharedDeviationFetch,
  PapiRequestDaSharedDeviationMultifetch,
  PapiUser,
} from '@wix/da-papi-types';
import { find as linkifyFind } from 'linkifyjs';
import { maximumAttachments } from '../../constants';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import { factoryAttachmentFromDeviationAndType } from '../../utils/attachments';
import { addDenyUserList } from '../actions/denyUserList';
import { getDenyUserList } from '../selectors/denyUserList';
import { AttachmentType, MessageAttachment, UnfurlLogEntry } from '../../types';

export type LinkifyLink = ReturnType<typeof linkifyFind>[0];

/**
 * Base URL is occasionally used in comparisons because linkify can return a different href value if it fails to determine the protocol.
 *
 * e.g. Linkify's `href` field will change from "https://youtu.be/a" to "http://youtu.be/a" if someone adds "abc"to the front like "abchttps://youtu.be/a"
 * Pushing this onto the log entry list would result in a duplicate.
 */
function getHrefUrlWithoutProtocol(href: string) {
  return href.replace(/(^\w+:|^)\/\//, '');
}

function canPlayLink(link: string) {
  try {
    const url = new URL(link);
    const hostname = url.hostname.toLowerCase();

    return (
      hostname.includes('youtube.com') ||
      hostname.includes('youtu.be') ||
      hostname.includes('dailymotion.com') ||
      hostname.includes('dai.ly') ||
      hostname.includes('vimeo.com') ||
      hostname.includes('twitch.tv') ||
      hostname.includes('soundcloud.com')
    );
  } catch (e) {
    return false;
  }
}

// Reconciles between current editor links, previous unfurl log, and current attachments
export function computeUnfurlReconciliation(
  currentUrlLinks: LinkifyLink[],
  unfurlLog: UnfurlLogEntry[],
  currentAttachments: MessageAttachment[],
  maximum: number
) {
  const overlaps = (
    aStart: number,
    aEnd: number,
    bStart: number,
    bEnd: number
  ) => aStart < bEnd && aEnd > bStart;

  const usedCurrent = new Set<number>();
  const continuedEntries: UnfurlLogEntry[] = [];
  const modifiedPairs: { prev: UnfurlLogEntry; cur: LinkifyLink }[] = [];

  // Pair previous entries with current links by overlap; either preserve or mark as modified
  unfurlLog.forEach(prev => {
    const idx = currentUrlLinks.findIndex(l =>
      overlaps(prev.start, prev.end, l.start, l.end)
    );
    if (idx === -1) {
      return; // treat as deleted; removed later
    }

    const cur = currentUrlLinks[idx];
    usedCurrent.add(idx);

    if (
      getHrefUrlWithoutProtocol(cur.href) ===
      getHrefUrlWithoutProtocol(prev.href)
    ) {
      continuedEntries.push({
        href: prev.href,
        start: cur.start,
        end: cur.end,
        // If kind missing (older logs), infer from href for future edit behavior
        kind: prev.kind ?? (canPlayLink(prev.href) ? 'video' : 'deviation'),
      });
    } else {
      modifiedPairs.push({ prev, cur });
    }
  });

  // We remove attachments for deleted entries and for modified ones (old href)
  const hrefsToRemove = new Set<string>([
    ...unfurlLog
      .filter(
        prev =>
          currentUrlLinks.findIndex(l =>
            overlaps(prev.start, prev.end, l.start, l.end)
          ) === -1
      )
      .map(e => getHrefUrlWithoutProtocol(e.href)),
    ...modifiedPairs.map(p => getHrefUrlWithoutProtocol(p.prev.href)),
  ]);

  const removalIndices: number[] = [];
  if (hrefsToRemove.size > 0) {
    currentAttachments.forEach((att, index) => {
      if (hrefsToRemove.has(getHrefUrlWithoutProtocol(att.link))) {
        removalIndices.push(index);
      }
    });
  }

  const currentCount = currentAttachments.length;
  const availableSlots = Math.max(
    0,
    maximum - (currentCount - removalIndices.length)
  );

  // Build add candidates in editor order
  const modifiedIndexSet = new Set<number>(
    modifiedPairs.map(p => currentUrlLinks.findIndex(l => l === p.cur))
  );
  const orderedCandidates: LinkifyLink[] = [];
  currentUrlLinks.forEach((link, idx) => {
    if (modifiedIndexSet.has(idx)) {
      orderedCandidates.push(link);
    } else if (!usedCurrent.has(idx)) {
      orderedCandidates.push(link);
    }
  });

  // Convert ordered candidates into typed add operations
  const orderedAdds: (
    | { type: 'video'; href: string; log: UnfurlLogEntry }
    | {
        type: 'deviation';
        fetch: PapiRequestDaSharedDeviationFetch;
        log: UnfurlLogEntry;
      }
  )[] = [];
  orderedCandidates.forEach(link => {
    const devFetch = elmoSaysThisMightBeADeviationUrl(link.href);
    if (devFetch) {
      // Checking current attachments here handles the case where a user loads into chat with a draft
      // and the draft has a text link + matching deviation attachment. Our unfurl log will be missing that link.
      const attachment = currentAttachments.find(att => {
        switch (att.attachmentType) {
          case AttachmentType.ImageDeviation:
          case AttachmentType.NonImageDeviation:
            return att.link.includes(devFetch.deviationid.toString());
          default:
            return false;
        }
      });

      if (!attachment) {
        orderedAdds.push({
          type: 'deviation',
          fetch: devFetch,
          log: {
            href: link.href,
            start: link.start,
            end: link.end,
            kind: 'deviation',
          },
        });
      }
    } else if (canPlayLink(link.href)) {
      orderedAdds.push({
        type: 'video',
        href: link.href,
        log: {
          href: link.href,
          start: link.start,
          end: link.end,
          kind: 'video',
        },
      });
    }
  });

  // Build a set of hrefs that will remain after removals, to avoid re-adding duplicates
  const keptHrefSet = new Set<string>();
  if (continuedEntries.length > 0) {
    const removalIndexSet = new Set<number>(removalIndices);
    currentAttachments.forEach((att, idx) => {
      if (!removalIndexSet.has(idx)) {
        keptHrefSet.add(getHrefUrlWithoutProtocol(att.link));
      }
    });
  }

  // Dedupe adds by href and skip any href already kept
  const seenNewHrefs = new Set<string>();
  const dedupedAdds = orderedAdds.filter(op => {
    const hrefValue = getHrefUrlWithoutProtocol(op.log.href);
    if (keptHrefSet.has(hrefValue)) {
      return false;
    }
    if (seenNewHrefs.has(hrefValue)) {
      return false;
    }
    seenNewHrefs.add(hrefValue);
    return true;
  });

  // Only take links up to available attachment slots
  const selectedAdds = dedupedAdds.slice(0, availableSlots);

  // Separate out video and deviation operations for easier processing
  const { videosToUse, deviationFetches, deviationLogs } = selectedAdds.reduce(
    (acc, op) => {
      if (op.type === 'video') {
        acc.videosToUse.push({ href: op.href, log: op.log });
      } else if (op.type === 'deviation') {
        acc.deviationFetches.push(op.fetch);
        acc.deviationLogs.push(op.log);
      }
      return acc;
    },
    {
      deviationFetches: [] as PapiRequestDaSharedDeviationFetch[],
      deviationLogs: [] as UnfurlLogEntry[],
      videosToUse: [] as { href: string; log: UnfurlLogEntry }[],
    }
  );

  return {
    continuedEntries,
    removalIndices,
    videosToUse,
    deviationFetches,
    deviationLogs,
  };
}

function* fetchDeviations(deviations: PapiRequestDaSharedDeviationFetch[]) {
  if (!deviations || deviations.length === 0) {
    return;
  }

  yield put(markUnfurlResolving());

  try {
    const params: PapiRequestDaSharedDeviationMultifetch = {
      deviationids: deviations.map(d => d.deviationid),
    };
    const result: PapiDaSharedDeviationMultifetch = yield call(
      requestPuppy,
      {
        method: 'get',
        url: `/deviation/multifetch`,
        params,
      },
      undefined
    );

    if (result && !isApiError(result)) {
      const notFound = deviations.filter(
        d =>
          !result.deviations.some(
            r =>
              r.deviationId === d.deviationid ||
              r.stashPrivateid === d.deviationid
          )
      );
      if (notFound.length > 0) {
        yield put(
          logDeviationsNotFound(notFound.map(deviation => deviation.deviationid))
        );
      }
      return result.deviations.map(d => factoryAttachmentFromDeviationAndType(d));
    }
  } catch (e) {
    // Fall through to markUnfurlFinished + return []
  }

  yield put(markUnfurlFinished());
  return [];
}

function* handleLinkUnfurl(action: ReturnType<typeof maybeUnfurlLinks>) {
  try {
  const unfurlLog: ReturnType<typeof getUnfurlLogForRoom> = yield select(
    state => getUnfurlLogForRoom(state, action.payload.room_id)
  );
  const currentAttachments: ReturnType<typeof getDraftMessageAttachments> =
    yield select(state =>
      getDraftMessageAttachments(state, action.payload.room_id)
    );
  const unfurlResolving: ReturnType<typeof getIsUnfurlResolving> = yield select(
    state => getIsUnfurlResolving(state)
  );

  if (unfurlResolving) {
    return; // Wait for resolve to complete
  }

  const currentUrlLinks: LinkifyLink[] = action.payload.links.filter(
    l => l.type === 'url'
  );

  const {
    continuedEntries,
    removalIndices,
    videosToUse,
    deviationFetches,
    deviationLogs,
  } = computeUnfurlReconciliation(
    currentUrlLinks,
    unfurlLog,
    currentAttachments,
    maximumAttachments
  );

  if (removalIndices.length > 0) {
    yield all(
      removalIndices
        .sort((a, b) => b - a)
        .map(idx => put(removeDraftAttachment(action.payload.room_id, idx)))
    );
  }

  const logsToAdd: UnfurlLogEntry[] = [];
  if (videosToUse.length > 0) {
    yield all(
      videosToUse.map(vc =>
        put(
          addDraftAttachment(
            {
              attachmentType: AttachmentType.Video,
              isVideo: true,
              link: vc.href,
            },
            action.payload.room_id
          )
        )
      )
    );
    logsToAdd.push(...videosToUse.map(v => v.log));
  }

  const priorDeviationsNotFound: ReturnType<typeof getDeviationsNotFound> =
    yield select(getDeviationsNotFound);
  const newDeviationFetches = deviationFetches.filter(
    d => !priorDeviationsNotFound.includes(d.deviationid)
  );
  if (newDeviationFetches.length > 0) {
    const fetched = yield call(fetchDeviations, newDeviationFetches);
    const deviationAttachments = fetched ?? [];
    if (deviationAttachments && deviationAttachments.length > 0) {
      yield all(
        deviationAttachments.map(att =>
          put(addDraftAttachment(att, action.payload.room_id))
        )
      );
      logsToAdd.push(...deviationLogs.slice(0, deviationAttachments.length));
    }
  }

  yield put(
    replaceUnfurlLog(action.payload.room_id, [
      ...continuedEntries,
      ...logsToAdd,
    ])
  );
  } catch (e) {
    yield put(markUnfurlFinished());
  }
}

function* handleAddDraftUser(action: ReturnType<typeof addDraftUser>) {
  const denyUserList: { [userId: string]: boolean } =
    yield select(getDenyUserList);

  let wasDenied: boolean = false;
  if (!denyUserList[action.payload.user.username]) {
    try {
      const params: PapiRequestDamzCanMessage = {
        username: action.payload.user.username,
      };
      const result: PapiDamzCanMessage = yield call(
        requestPuppy,
        {
          method: 'get',
          url: `/can_message`,
          params,
        },
        undefined,
        'damz'
      );

      if (result && !isApiError(result) && result.canMessage === false) {
        wasDenied = true;
        yield put(addDenyUserList(params.username));
      }
    } catch (e) {
      // Log and continue; user will not be marked denied
    }
  }

  if (wasDenied || denyUserList[action.payload.user.username]) {
    yield put(disallowDraftMessage(action.payload.room_id));
  }
}

function* handleRemoveDraftUser(action: ReturnType<typeof removeDraftUser>) {
  const participants: PapiUser[] = yield select(getDraftParticipants);
  const denyUserList: { [userId: string]: boolean } =
    yield select(getDenyUserList);

  const isDenied = participants.some(
    participant => denyUserList[participant.username]
  );

  if (isDenied) {
    yield put(disallowDraftMessage(action.payload.room_id));
  } else {
    yield put(allowDraftMessage(action.payload.room_id));
  }
}

/**
 * Handles draft related things like unfurling and LS storage
 */
export function* draftsSaga() {
  yield take(damzOnline); // wait for online to happen
  yield all([
    debounce(250, maybeUnfurlLinks, handleLinkUnfurl),
    takeEvery(addDraftUser, handleAddDraftUser),
    takeEvery(removeDraftUser, handleRemoveDraftUser),
  ]);
}
